import { useState, useEffect, useCallback, useRef } from "react";
import api, { formatApiError, getToken, API } from "@/lib/api";
import BillViewer from "@/components/BillViewer";
import { cadenceInfo, STATUS_LABELS, STATUS_STYLE, NOT_INTERESTED_REASONS, reasonLabel, timeAgo, dateWithAgo, timeOfDay, money } from "@/lib/format";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import GooglePlaceSearch from "@/components/GooglePlaceSearch";
import { useConfirm } from "@/components/ConfirmProvider";
import {
  Star, Phone, MapPin, Navigation, Receipt, Loader2, Plus, Paperclip,
  History, ShoppingBag, RefreshCw, ExternalLink, User, MapPinCheck, ThumbsDown, MessageSquare, CalendarPlus, Pencil, Trash2, X, Check, FileWarning, Radar, ChevronLeft,
} from "lucide-react";

function Badge({ flag }) {
  const info = cadenceInfo(flag);
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${info.cls}`}>{info.label}</span>;
}

function VisitForm({ garageId, onDone }) {
  const confirm = useConfirm();
  const [manual, setManual] = useState(false);
  const [dt, setDt] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const checkinNow = async (force = false) => {
    setBusy(true);
    try {
      await api.post(`/garages/${garageId}/checkin`, force ? { force: true } : {});
      toast.success("Checked in");
      onDone();
    } catch (e) {
      if (e.response?.status === 409 && !force) {
        if (await confirm({ title: "Visit already recorded", description: "A visit is already recorded here today. Record another one?", confirmText: "Record another" })) return checkinNow(true);
      } else { toast.error(formatApiError(e.response?.data?.detail)); }
    } finally { setBusy(false); }
  };

  const saveManual = async (e, force = false) => {
    if (e) e.preventDefault();
    if (!dt) { toast.error("Pick a date and time"); return; }
    setBusy(true);
    try {
      await api.post(`/garages/${garageId}/checkin`, { timestamp: new Date(dt).toISOString(), note: note || undefined, force });
      toast.success("Visit recorded");
      setManual(false); setDt(""); setNote("");
      onDone();
    } catch (e2) {
      if (e2.response?.status === 409 && !force) {
        if (await confirm({ title: "Visit already recorded", description: "A visit is already recorded for that day. Save anyway?", confirmText: "Save anyway" })) return saveManual(null, true);
      } else { toast.error(formatApiError(e2.response?.data?.detail)); }
    } finally { setBusy(false); }
  };

  const inp = "w-full h-11 px-3 rounded-md border border-[#D1D5DB] bg-white focus:outline-none focus:border-[#1E3A8A]";
  return (
    <div className="space-y-2" data-testid="visit-form">
      <button onClick={() => checkinNow()} disabled={busy} data-testid="detail-checkin-now"
        className="w-full h-12 rounded-md bg-[#1E3A8A] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#375DBA] active:scale-[0.98] transition-colors disabled:opacity-60">
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <MapPinCheck className="w-5 h-5" />} Check in now
      </button>
      {!manual ? (
        <button onClick={() => setManual(true)} data-testid="detail-record-visit"
          className="w-full h-11 rounded-md border border-[#D1D5DB] font-bold text-sm flex items-center justify-center gap-2 text-[#374151]">
          <CalendarPlus className="w-4 h-4" /> Record a visit (pick date &amp; time)
        </button>
      ) : (
        <form onSubmit={saveManual} className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-3 space-y-2" data-testid="manual-visit-form">
          <input type="datetime-local" className={inp} value={dt} onChange={(e) => setDt(e.target.value)} data-testid="visit-datetime" required />
          <input className={inp} placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="visit-note" />
          <div className="flex gap-2">
            <button type="button" onClick={() => setManual(false)} className="flex-1 h-11 rounded-md border border-[#D1D5DB] font-bold text-sm">Cancel</button>
            <button type="submit" disabled={busy} data-testid="visit-save"
              className="flex-1 h-11 rounded-md bg-[#1E3A8A] text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />} Save visit
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function PurchaseForm({ garageId, onDone }) {
  const [f, setF] = useState({ amount: "", commission: "", invoice_number: "", date: "", note: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.amount) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("amount", f.amount);
    if (f.commission) fd.append("commission", f.commission);
    if (f.invoice_number) fd.append("invoice_number", f.invoice_number);
    if (f.date) fd.append("date", new Date(f.date).toISOString());
    if (f.note) fd.append("note", f.note);
    if (file) fd.append("file", file);
    try {
      await api.post(`/garages/${garageId}/purchases`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Order recorded");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inp = "w-full h-11 px-3 rounded-md border border-[#D1D5DB] bg-white focus:outline-none focus:border-[#1E3A8A]";
  const lbl = "block text-xs font-bold uppercase tracking-wide text-[#6B7280] mb-1";
  return (
    <form onSubmit={submit} className="space-y-3 bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-4" data-testid="purchase-form">
      <div className="grid grid-cols-2 gap-3">
        <div><label className={lbl}>Amount (AED) *</label>
          <input className={inp} required type="number" inputMode="decimal" data-testid="purchase-amount" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} /></div>
        <div><label className={lbl}>Commission (AED)</label>
          <input className={inp} type="number" inputMode="decimal" data-testid="purchase-commission" value={f.commission} onChange={(e) => setF({ ...f, commission: e.target.value })} placeholder="Optional" /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={lbl}>Invoice #</label>
          <input className={inp} data-testid="purchase-invoice" value={f.invoice_number} onChange={(e) => setF({ ...f, invoice_number: e.target.value })} /></div>
        <div><label className={lbl}>Date</label>
          <input className={inp} type="date" data-testid="purchase-date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
      </div>
      <div><label className={lbl}>Note</label>
        <input className={inp} data-testid="purchase-note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></div>
      <div>
        <label className={lbl}>Invoice photo / PDF</label>
        <label className="flex items-center gap-2 h-11 px-3 rounded-md border border-dashed border-[#D1D5DB] bg-white cursor-pointer text-sm text-[#6B7280]">
          <Paperclip className="w-4 h-4" />
          {file ? file.name : "Attach a photo or PDF"}
          <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="purchase-file" onChange={(e) => setFile(e.target.files[0])} />
        </label>
      </div>
      <button type="submit" disabled={busy} data-testid="purchase-submit"
        className="w-full h-12 rounded-md bg-[#1E3A8A] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#375DBA] active:scale-[0.98] transition-colors disabled:opacity-60">
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Receipt className="w-5 h-5" />} Save order
      </button>
    </form>
  );
}

export default function GarageDetail({ garageId, open, onClose, isManager, onChanged, onOpenDetail }) {
  const confirm = useConfirm();
  const [garage, setGarage] = useState(null);
  const [history, setHistory] = useState({ visits: [], purchases: [] });
  const [tab, setTab] = useState("overview");
  const [showPurchase, setShowPurchase] = useState(false);
  const [linking, setLinking] = useState(false);
  const [showNi, setShowNi] = useState(false);
  const [nearby, setNearby] = useState([]);
  const [editing, setEditing] = useState(false);
  const [info, setInfo] = useState({});
  const [editVisitId, setEditVisitId] = useState(null);
  const [ve, setVe] = useState({ timestamp: "", note: "" });
  const [editOrderId, setEditOrderId] = useState(null);
  const [viewItem, setViewItem] = useState(null); // 3.2 — read-only order/visit view
  const [oe, setOe] = useState({ amount: "", date: "", commission: "", invoice_number: "", note: "" });

  const load = useCallback(async () => {
    if (!garageId) return;
    const [g, h] = await Promise.all([
      api.get(`/garages/${garageId}`),
      api.get(`/garages/${garageId}/history`),
    ]);
    setGarage(g.data);
    setHistory(h.data);
  }, [garageId]);

  useEffect(() => { if (open) { setTab("overview"); setShowPurchase(false); load(); } }, [open, load]);

  const linkGoogle = async (pid) => {
    pid = pid || garage?.google_place_id;
    if (!pid) { toast.error("Search and pick a business"); return; }
    setLinking(true);
    try {
      await api.post(`/garages/${garageId}/link-google`, { place_id: pid });
      toast.success("Google info linked");
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLinking(false); }
  };

  const afterPurchase = () => { setShowPurchase(false); load(); onChanged && onChanged(); };

  const billInputRef = useRef(null);
  const [billTargetPid, setBillTargetPid] = useState(null);
  const [billView, setBillView] = useState(null);
  const pickBill = (pid) => { setBillTargetPid(pid); setTimeout(() => billInputRef.current?.click(), 0); };
  const onBillChosen = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f || !billTargetPid) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post(`/garages/${garageId}/purchases/${billTargetPid}/bill`, fd);
      toast.success("Bill attached");
      setViewItem((vi) => (vi && vi.type === "order" && vi.data.id === billTargetPid) ? { ...vi, data: { ...vi.data, invoice_file: data.invoice_file } } : vi);
      load(); onChanged && onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const afterVisit = () => { setTab("overview"); load(); onChanged && onChanged(); };

  const convert = async () => {
    if ((garage.visit_count || 0) < 1) { toast.error("Record a visit before creating the WhatsApp group"); return; }
    if (!(await confirm({ title: "Convert to My garages", description: `Move "${garage.name}" to My garages? This marks the WhatsApp group as created.`, confirmText: "Convert" }))) return;
    try {
      await api.patch(`/garages/${garageId}`, { status: "group_created" });
      toast.success("Moved to My garages");
      load(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const markNi = async (reason) => {
    try {
      await api.patch(`/garages/${garageId}`, { status: "not_interested", not_interested_reason: reason });
      toast.success("Marked not interested");
      setShowNi(false); load(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    if (garage?.status === "group_created" && garage.latitude != null) {
      api.get("/garages/nearby", { params: { lat: garage.latitude, lng: garage.longitude, radius_km: 50 } })
        .then((r) => setNearby((r.data || []).filter((x) => x.id !== garageId && x.status !== "group_created")
          .sort((a, b) => (a.distance_km ?? 9e9) - (b.distance_km ?? 9e9)).slice(0, 3)))
        .catch(() => setNearby([]));
    } else { setNearby([]); }
  }, [garage, garageId]);

  const startEditInfo = () => {
    setInfo({ name: garage.name || "", area: garage.area || "", phone: garage.phone || "",
      buyer_name: garage.buyer_name || "", notes: garage.notes || "" });
    setEditing(true);
  };
  const saveInfo = async () => {
    try {
      await api.patch(`/garages/${garageId}`, info);
      toast.success("Details saved"); setEditing(false); load(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delVisit = async (vid) => {
    if (!(await confirm({ title: "Delete visit", description: "Delete this visit? This cannot be undone.", confirmText: "Delete", destructive: true }))) return;
    try { await api.delete(`/garages/${garageId}/visits/${vid}`); toast.success("Visit deleted"); load(); onChanged && onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startEditVisit = (v) => { setEditVisitId(v.id); setVe({ timestamp: v.timestamp ? new Date(v.timestamp).toISOString().slice(0, 16) : "", note: v.note || "" }); };
  const saveVisit = async (vid) => {
    try {
      await api.patch(`/garages/${garageId}/visits/${vid}`, { timestamp: ve.timestamp ? new Date(ve.timestamp).toISOString() : undefined, note: ve.note });
      toast.success("Visit updated"); setEditVisitId(null); load(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delOrder = async (pid) => {
    if (!(await confirm({ title: "Delete order", description: "Delete this order? This cannot be undone.", confirmText: "Delete", destructive: true }))) return;
    try { await api.delete(`/garages/${garageId}/purchases/${pid}`); toast.success("Order deleted"); load(); onChanged && onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startEditOrder = (p) => { setEditOrderId(p.id); setOe({ amount: p.amount ?? "", date: p.date ? new Date(p.date).toISOString().slice(0, 10) : "", commission: p.commission ?? "", invoice_number: p.invoice_number || "", note: p.note || "" }); };
  const saveOrder = async (pid) => {
    try {
      await api.patch(`/garages/${garageId}/purchases/${pid}`, {
        amount: oe.amount !== "" ? Number(oe.amount) : undefined,
        date: oe.date ? new Date(oe.date).toISOString() : undefined,
        commission: oe.commission !== "" ? Number(oe.commission) : undefined,
        invoice_number: oe.invoice_number, note: oe.note,
      });
      toast.success("Order updated"); setEditOrderId(null); load(); onChanged && onChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // 2.4 — Navigate opens the Google Business listing (photos/hours/reviews), never driving directions
  const mapsUrl = garage?.google_maps_uri
    || (garage?.google_place_id
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(garage.name || "")}&query_place_id=${garage.google_place_id}`
      : (garage?.name
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${garage.name} ${garage.area || ""}`.trim())}`
        : (garage?.latitude != null ? `https://www.google.com/maps/search/?api=1&query=${garage.latitude},${garage.longitude}` : null)));

  const TabBtn = ({ id, icon: Icon, label }) => (
    <button onClick={() => setTab(id)} data-testid={`detail-tab-${id}`}
      className={`flex-1 h-10 flex items-center justify-center gap-1.5 text-sm font-bold border-b-2 transition-colors ${tab === id ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-[#9CA3AF]"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto p-0 [&>button.absolute.right-4.top-4]:hidden" data-testid="garage-detail">
        {!garage ? (
          <div className="h-full flex items-center justify-center">
            <SheetTitle className="sr-only">Loading garage</SheetTitle>
            <Loader2 className="w-7 h-7 animate-spin text-[#1E3A8A]" />
          </div>
        ) : (
          <>
            <SheetHeader className="p-5 pb-3 bg-[#1E3A8A] text-white">
              <div className="flex items-center justify-between gap-2 mb-2">
                <button onClick={onClose} data-testid="detail-back" aria-label="Back"
                  className="flex items-center gap-1 h-11 -ml-2 pl-1 pr-3 rounded-lg hover:bg-white/15 active:scale-95 text-white font-bold text-sm transition-colors">
                  <ChevronLeft className="w-6 h-6" strokeWidth={2.5} /> Back
                </button>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${STATUS_STYLE[garage.display_status]}`}>{STATUS_LABELS[garage.display_status]}</span>
                  <button onClick={onClose} data-testid="detail-close" aria-label="Close"
                    className="w-11 h-11 rounded-full bg-white/20 hover:bg-white/35 active:scale-95 flex items-center justify-center text-white shrink-0 transition-colors">
                    <X className="w-5 h-5" strokeWidth={2.5} />
                  </button>
                </div>
              </div>
              <SheetTitle className="text-white text-2xl font-black leading-tight">{garage.name}</SheetTitle>
              <div className="text-sm text-white/80 flex items-center gap-1"><MapPin className="w-4 h-4" /> {garage.area}</div>
            </SheetHeader>

            <div className="flex border-b border-[#E5E7EB] px-2">
              <TabBtn id="overview" icon={User} label="Overview" />
              <TabBtn id="orders" icon={ShoppingBag} label={`Orders (${history.purchases.length})`} />
              <TabBtn id="visits" icon={History} label={`Visits (${history.visits.length})`} />
            </div>

            <div className="p-5 space-y-4">
              {tab === "overview" && (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-3 text-center">
                      <div className="text-2xl font-black text-[#111827]">{garage.visit_count}</div>
                      <div className="text-xs font-bold uppercase text-[#6B7280]">Visits</div>
                    </div>
                    <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-3 text-center">
                      <div className="text-2xl font-black text-[#111827]">{garage.purchase_count}</div>
                      <div className="text-xs font-bold uppercase text-[#6B7280]">Orders</div>
                    </div>
                    <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-3 text-center">
                      <div className="text-lg font-black text-[#047857]">{money(garage.total_purchase_value)}</div>
                      <div className="text-xs font-bold uppercase text-[#6B7280]">Total</div>
                    </div>
                  </div>
                  {(garage.total_commission || 0) > 0 && (
                    <div className="flex items-center justify-between bg-[#FFFBEB] border border-[#FCD34D] rounded-lg px-3 py-2 text-sm" data-testid="commission-total">
                      <span className="font-bold uppercase text-[11px] tracking-wide text-[#B45309]">Commission total</span>
                      <span className="font-black text-[#B45309]">{money(garage.total_commission)}</span>
                    </div>
                  )}

                  {garage.status === "group_created" && (
                    <div className="pt-2 border-t border-[#E5E7EB]" data-testid="nearby-prospects">
                      <div className="text-xs font-bold uppercase text-[#6B7280] mb-1.5 flex items-center gap-1.5"><Radar className="w-3.5 h-3.5 text-[#1E3A8A]" /> Nearest prospects</div>
                      {nearby.length === 0 ? (
                        <p className="text-sm text-[#9CA3AF]" data-testid="nearby-none">No other prospects in the database near here yet.</p>
                      ) : (
                      <div className="space-y-1.5">
                        {nearby.map((p) => (
                          <button key={p.id} onClick={() => onOpenDetail && onOpenDetail(p.id)} data-testid={`nearby-${p.id}`}
                            className="w-full flex items-center gap-2 text-left p-2 rounded-lg border border-[#E5E7EB] hover:border-[#93C5FD]">
                            <span className="w-8 h-8 rounded-md bg-[#111827] text-white text-xs font-black flex items-center justify-center shrink-0">{p.opportunity_score ?? "—"}</span>
                            <span className="min-w-0 flex-1">
                              <span className="block font-bold text-sm text-[#111827] truncate">{p.name}</span>
                              <span className="block text-xs text-[#6B7280] truncate">{p.area} · {p.distance_km} km</span>
                            </span>
                          </button>
                        ))}
                      </div>
                      )}
                    </div>
                  )}

                  {editing ? (
                    <div className="space-y-2" data-testid="edit-garage-form">
                      {[["Name", "name", "edit-name"], ["Area", "area", "edit-area"], ["Phone", "phone", "edit-phone"], ["Buyer name", "buyer_name", "edit-buyer"]].map(([lbl, key, tid]) => (
                        <div key={key}>
                          <label className="block text-xs font-bold uppercase text-[#6B7280] mb-1">{lbl}</label>
                          <input data-testid={tid} className="w-full h-11 px-3 rounded-md border border-[#D1D5DB] focus:outline-none focus:border-[#1E3A8A]"
                            value={info[key]} onChange={(e) => setInfo({ ...info, [key]: e.target.value })} />
                        </div>
                      ))}
                      <div>
                        <label className="block text-xs font-bold uppercase text-[#6B7280] mb-1">Note</label>
                        <textarea data-testid="edit-notes" className="w-full min-h-[60px] px-3 py-2 rounded-md border border-[#D1D5DB]" value={info.notes} onChange={(e) => setInfo({ ...info, notes: e.target.value })} />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => setEditing(false)} className="flex-1 h-11 rounded-md border border-[#D1D5DB] font-bold text-sm">Cancel</button>
                        <button onClick={saveInfo} data-testid="edit-save" className="flex-1 h-11 rounded-md bg-[#1E3A8A] text-white font-bold text-sm flex items-center justify-center gap-2"><Check className="w-4 h-4" /> Save details</button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-end -mb-1">
                        <button onClick={startEditInfo} data-testid="edit-garage-btn" className="text-xs font-bold text-[#1E3A8A] flex items-center gap-1"><Pencil className="w-3.5 h-3.5" /> Edit details</button>
                      </div>
                      {garage.buyer_name && <Row label="Buyer" value={garage.buyer_name} />}
                      {garage.phone && <Row label="Phone" value={garage.phone} icon={Phone} />}
                      {garage.address && <Row label="Address" value={garage.address} />}
                      <Row label="Last visit" value={dateWithAgo(garage.last_visit_date, "never")} />
                      <Row label="Last order" value={dateWithAgo(garage.last_purchase_date, "no orders yet")} />
                      {(garage.google_rating != null) && <Row label="Google" value={`★ ${garage.google_rating} · ${garage.google_review_count || 0} reviews`} icon={Star} />}
                      {garage.brand_focus && <Row label="Brand focus" value={garage.brand_focus} />}
                      {garage.notes && <Row label="Notes" value={garage.notes} />}
                      {garage.opening_hours && garage.opening_hours.length > 0 && (
                        <div className="pt-1">
                          <div className="text-xs font-bold uppercase text-[#6B7280] mb-1">Opening hours</div>
                          {garage.opening_hours.map((h, i) => <div key={i} className="text-xs text-[#374151]">{h}</div>)}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex gap-2">
                    {mapsUrl && (
                      <a href={mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="detail-navigate"
                        className="flex-1 h-11 rounded-md border-2 border-[#1E3A8A] text-[#1E3A8A] font-bold flex items-center justify-center gap-2 active:scale-[0.98]">
                        <Navigation className="w-4 h-4" /> Navigate
                      </a>
                    )}
                    <button onClick={() => { setTab("orders"); setShowPurchase(true); }} data-testid="detail-record-order"
                      className="flex-1 h-11 rounded-md bg-[#1E3A8A] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#375DBA] active:scale-[0.98] transition-colors">
                      <Plus className="w-4 h-4" /> Record order
                    </button>
                  </div>

                  {garage.status !== "group_created" && (
                    <div className="pt-2 border-t border-[#E5E7EB] space-y-2" data-testid="prospect-actions">
                      <button onClick={convert} data-testid="detail-convert"
                        className="w-full h-11 rounded-md bg-[#047857] text-white font-bold flex items-center justify-center gap-2 active:scale-[0.98]">
                        <MessageSquare className="w-4 h-4" /> Convert — WhatsApp group created
                      </button>
                      {garage.status === "not_interested" ? (
                        <div className="text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] rounded-md px-3 py-2" data-testid="ni-current">
                          Not interested{garage.not_interested_reason ? ` · ${reasonLabel(garage.not_interested_reason)}` : ""}
                        </div>
                      ) : (
                        <button onClick={() => setShowNi((s) => !s)} data-testid="detail-not-interested"
                          className="w-full h-11 rounded-md border border-[#FECACA] text-[#B91C1C] font-bold flex items-center justify-center gap-2">
                          <ThumbsDown className="w-4 h-4" /> Mark not interested
                        </button>
                      )}
                      {showNi && (
                        <div className="space-y-2 pt-1" data-testid="ni-reasons">
                          {NOT_INTERESTED_REASONS.map((r) => (
                            <button key={r.value} onClick={() => markNi(r.value)} data-testid={`detail-ni-${r.value}`}
                              className="w-full text-left h-11 px-3 rounded-lg border border-[#E5E7EB] hover:border-[#B91C1C] font-medium text-sm text-[#111827]">
                              {r.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {isManager && (
                    <div className="pt-2 border-t border-[#E5E7EB]">
                      <div className="text-xs font-bold uppercase text-[#6B7280] mb-2">Google Business</div>
                      {garage.google_place_id ? (
                        <div className="flex items-center gap-2">
                          <button onClick={() => linkGoogle()} disabled={linking} data-testid="refresh-google"
                            className="flex items-center gap-2 h-10 px-3 rounded-md border border-[#D1D5DB] font-medium text-sm active:scale-95">
                            {linking ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh Google info
                          </button>
                          {garage.google_maps_uri && <a href={garage.google_maps_uri} target="_blank" rel="noreferrer" className="text-sm text-[#1E3A8A] font-medium flex items-center gap-1"><ExternalLink className="w-4 h-4" /> View listing</a>}
                        </div>
                      ) : (
                        <GooglePlaceSearch onSelect={(p) => linkGoogle(p.google_place_id)} placeholder="Search this garage on Google…" />
                      )}
                    </div>
                  )}
                </>
              )}

              {tab === "orders" && (
                <>
                  {!showPurchase && (
                    <button onClick={() => setShowPurchase(true)} data-testid="add-order-btn"
                      className="w-full h-11 rounded-md bg-[#1E3A8A] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#375DBA] active:scale-[0.98] transition-colors">
                      <Plus className="w-4 h-4" /> Record an order
                    </button>
                  )}
                  {showPurchase && <PurchaseForm garageId={garageId} onDone={afterPurchase} />}
                  {history.purchases.length === 0 ? (
                    <p className="text-center text-sm text-[#6B7280] py-6">No orders recorded yet.</p>
                  ) : history.purchases.map((p) => (
                    <div key={p.id} className={`border border-[#E5E7EB] rounded-lg p-3 ${editOrderId === p.id ? "" : "cursor-pointer hover:border-[#93C5FD]"}`} data-testid={`order-${p.id}`}
                      onClick={() => { if (editOrderId !== p.id) setViewItem({ type: "order", data: p }); }}>
                      {editOrderId === p.id ? (
                        <div className="space-y-2" data-testid={`order-edit-${p.id}`}>
                          <div className="grid grid-cols-2 gap-2">
                            <input type="number" placeholder="Amount" className="h-10 px-2 rounded-md border border-[#D1D5DB]" data-testid="order-edit-amount" value={oe.amount} onChange={(e) => setOe({ ...oe, amount: e.target.value })} />
                            <input type="date" className="h-10 px-2 rounded-md border border-[#D1D5DB]" data-testid="order-edit-date" value={oe.date} onChange={(e) => setOe({ ...oe, date: e.target.value })} />
                            <input type="number" placeholder="Commission" className="h-10 px-2 rounded-md border border-[#D1D5DB]" data-testid="order-edit-commission" value={oe.commission} onChange={(e) => setOe({ ...oe, commission: e.target.value })} />
                            <input placeholder="Invoice #" className="h-10 px-2 rounded-md border border-[#D1D5DB]" value={oe.invoice_number} onChange={(e) => setOe({ ...oe, invoice_number: e.target.value })} />
                          </div>
                          <input placeholder="Note" className="w-full h-10 px-2 rounded-md border border-[#D1D5DB]" value={oe.note} onChange={(e) => setOe({ ...oe, note: e.target.value })} />
                          <div className="flex gap-2">
                            <button onClick={() => setEditOrderId(null)} className="flex-1 h-10 rounded-md border border-[#D1D5DB] font-bold text-sm">Cancel</button>
                            <button onClick={() => saveOrder(p.id)} data-testid={`order-save-${p.id}`} className="flex-1 h-10 rounded-md bg-[#1E3A8A] text-white font-bold text-sm">Save</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between">
                            <div className="text-lg font-black text-[#047857]">{money(p.amount)}</div>
                            <div className="flex items-center gap-2">
                              <div className="text-xs text-[#6B7280]">{timeAgo(p.date)}</div>
                              <button onClick={(e) => { e.stopPropagation(); startEditOrder(p); }} data-testid={`order-edit-btn-${p.id}`} className="p-1.5 text-[#6B7280] hover:text-[#1E3A8A]"><Pencil className="w-4 h-4" /></button>
                              <button onClick={(e) => { e.stopPropagation(); delOrder(p.id); }} data-testid={`order-delete-${p.id}`} className="p-1.5 text-[#9CA3AF] hover:text-[#B91C1C]"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </div>
                          <div className="text-xs text-[#6B7280] mt-1 flex flex-wrap gap-x-3">
                            {p.commission != null && p.commission !== "" && <span className="font-bold text-[#B45309]">Commission {money(p.commission)}</span>}
                            {p.invoice_number && <span>Inv: {p.invoice_number}</span>}
                            <span>By {p.entered_by}</span>
                          </div>
                          {p.note && <div className="text-sm text-[#374151] mt-1">{p.note}</div>}
                          <div className="flex flex-wrap items-center gap-2 mt-2">
                            {p.invoice_file ? (
                              <button onClick={(e) => { e.stopPropagation(); setBillView(p.invoice_file); }} data-testid={`view-bill-${p.id}`}
                                className="inline-flex items-center gap-1 min-h-[44px] px-3 rounded-md border border-[#93C5FD] text-[#1E3A8A] text-sm font-bold">
                                <Paperclip className="w-4 h-4" /> View bill
                              </button>
                            ) : (
                              <button onClick={(e) => { e.stopPropagation(); pickBill(p.id); }} data-testid={`upload-bill-${p.id}`}
                                className="inline-flex items-center gap-1 min-h-[44px] px-3 rounded-md bg-[#B45309] text-white text-sm font-bold">
                                <FileWarning className="w-4 h-4" /> Upload bill
                              </button>
                            )}
                            {!p.invoice_number && (
                              <span className="inline-flex items-center gap-1 text-xs font-bold text-[#B45309] bg-[#FEF3C7] border border-[#FCD34D] rounded-md px-2 py-1" data-testid={`missing-inv-${p.id}`}>
                                <Receipt className="w-3.5 h-3.5" /> Missing invoice no.
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </>
              )}

              {tab === "visits" && (
                <>
                  <VisitForm garageId={garageId} onDone={afterVisit} />
                  {history.visits.length === 0 ? (
                    <p className="text-center text-sm text-[#6B7280] py-6">No visits yet.</p>
                  ) : history.visits.map((v) => (
                    <div key={v.id} className={`border border-[#E5E7EB] rounded-lg p-3 ${editVisitId === v.id ? "" : "cursor-pointer hover:border-[#93C5FD]"}`} data-testid={`visit-${v.id}`}
                      onClick={() => { if (editVisitId !== v.id) setViewItem({ type: "visit", data: v }); }}>
                      {editVisitId === v.id ? (
                        <div className="space-y-2" data-testid={`visit-edit-${v.id}`}>
                          <input type="datetime-local" className="w-full h-10 px-2 rounded-md border border-[#D1D5DB]" data-testid="visit-edit-datetime" value={ve.timestamp} onChange={(e) => setVe({ ...ve, timestamp: e.target.value })} />
                          <input placeholder="Note" className="w-full h-10 px-2 rounded-md border border-[#D1D5DB]" value={ve.note} onChange={(e) => setVe({ ...ve, note: e.target.value })} />
                          <div className="flex gap-2">
                            <button onClick={() => setEditVisitId(null)} className="flex-1 h-10 rounded-md border border-[#D1D5DB] font-bold text-sm">Cancel</button>
                            <button onClick={() => saveVisit(v.id)} data-testid={`visit-save-edit-${v.id}`} className="flex-1 h-10 rounded-md bg-[#1E3A8A] text-white font-bold text-sm">Save</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between">
                            <div className="font-bold text-[#111827] capitalize">{v.outcome || "Visit"}</div>
                            <div className="flex items-center gap-2">
                              <div className="text-xs text-[#6B7280]">{timeAgo(v.timestamp)}</div>
                              <button onClick={(e) => { e.stopPropagation(); startEditVisit(v); }} data-testid={`visit-edit-btn-${v.id}`} className="p-1.5 text-[#6B7280] hover:text-[#1E3A8A]"><Pencil className="w-4 h-4" /></button>
                              <button onClick={(e) => { e.stopPropagation(); delVisit(v.id); }} data-testid={`visit-delete-${v.id}`} className="p-1.5 text-[#9CA3AF] hover:text-[#B91C1C]"><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </div>
                          <div className="text-xs text-[#6B7280] mt-0.5">By {v.by}</div>
                          {v.note && <div className="text-sm text-[#374151] mt-1">{v.note}</div>}
                        </>
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* 3.2 — read-only view of a single order / visit */}
            <Dialog open={!!viewItem} onOpenChange={(v) => { if (!v) setViewItem(null); }}>
              <DialogContent className="max-w-sm" data-testid="record-view-dialog">
                <DialogHeader>
                  <DialogTitle className="text-lg font-black text-[#1E3A8A]">
                    {viewItem?.type === "order" ? "Order details" : "Visit details"}
                  </DialogTitle>
                </DialogHeader>
                {viewItem?.type === "order" && (
                  <div className="space-y-2 text-sm" data-testid="view-order">
                    <Row label="Amount" value={money(viewItem.data.amount)} />
                    {viewItem.data.commission != null && viewItem.data.commission !== "" && <Row label="Commission" value={money(viewItem.data.commission)} />}
                    <Row label="Invoice #" value={viewItem.data.invoice_number || "—"} />
                    <Row label="Date" value={`${dateWithAgo(viewItem.data.date)} · ${timeOfDay(viewItem.data.date)}`} />
                    <Row label="Entered by" value={viewItem.data.entered_by || "—"} />
                    {viewItem.data.note && <Row label="Note" value={viewItem.data.note} />}
                    {viewItem.data.invoice_file ? (
                      <div className="pt-1" data-testid="view-order-bill-block">
                        <div className="text-xs font-bold uppercase text-[#6B7280] mb-1">Bill</div>
                        <button onClick={() => setBillView(viewItem.data.invoice_file)} data-testid="view-order-bill"
                          className="w-full border border-[#E5E7EB] rounded-lg overflow-hidden hover:border-[#93C5FD] min-h-[44px]">
                          {((viewItem.data.invoice_file.content_type || "").includes("pdf") || (viewItem.data.invoice_file.filename || "").toLowerCase().endsWith(".pdf"))
                            ? <div className="flex items-center gap-2 p-3 text-sm font-bold text-[#1E3A8A]"><Paperclip className="w-4 h-4" /> {viewItem.data.invoice_file.filename || "PDF bill"} — tap to open</div>
                            : <img src={`${API}/files/${viewItem.data.invoice_file.path}?auth=${getToken()}`} alt="bill" className="w-full max-h-56 object-contain bg-[#F9FAFB]" />}
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => pickBill(viewItem.data.id)} data-testid="view-order-upload"
                        className="w-full min-h-[44px] rounded-lg bg-[#B45309] text-white font-bold text-sm flex items-center justify-center gap-2">
                        <FileWarning className="w-4 h-4" /> Upload bill
                      </button>
                    )}
                  </div>
                )}
                {viewItem?.type === "visit" && (
                  <div className="space-y-2 text-sm" data-testid="view-visit">
                    <Row label="Outcome" value={<span className="capitalize">{viewItem.data.outcome || "Visit"}</span>} />
                    <Row label="When" value={`${dateWithAgo(viewItem.data.timestamp)} · ${timeOfDay(viewItem.data.timestamp)}`} />
                    <Row label="By" value={viewItem.data.by || "—"} />
                    {viewItem.data.note && <Row label="Note" value={viewItem.data.note} />}
                  </div>
                )}
              </DialogContent>
            </Dialog>

            <input ref={billInputRef} type="file" accept="image/*,application/pdf" hidden onChange={onBillChosen} data-testid="bill-file-input" />
            <BillViewer file={billView} onClose={() => setBillView(null)} />
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Row({ label, value, icon: Icon }) {
  return (
    <div className="flex justify-between items-start gap-4">
      <span className="text-[#6B7280] flex items-center gap-1.5">{Icon && <Icon className="w-4 h-4" />}{label}</span>
      <span className="font-bold text-[#111827] text-right">{value}</span>
    </div>
  );
}
